import { useEffect, useState } from 'react'

import { useDebouncedValue } from '#/shared/lib/hooks'
import { SEARCH_DEBOUNCE_MS } from './constants'
import { useTableSearchParams } from './search-params'

export interface UseTableSearchResult {
  value: string
  onChange: (value: string) => void
}

/**
 * Drives a toolbar search input against the URL-backed `q` search param.
 * The input value stays local so typing feels instant; once the debounce
 * settles the trimmed value is pushed to the URL (where `useLeadsPage`
 * reads it to drive the API). Reverse-syncs on Back / Forward so the input
 * reflects wherever the user navigates to.
 */
export function useTableSearch(): UseTableSearchResult {
  const { search: committed, setSearch } = useTableSearchParams()
  const [value, setValue] = useState(committed)
  const debounced = useDebouncedValue(value, SEARCH_DEBOUNCE_MS)

  // URL → input (external navigation, reload with ?q=…).
  useEffect(() => {
    setValue(committed)
  }, [committed])

  // input → URL (debounced). Compare first so we don't navigate when the
  // trimmed value already matches what's committed.
  useEffect(() => {
    const next = debounced.trim()
    if (next !== committed) setSearch(next)
  }, [debounced, committed, setSearch])

  return { value, onChange: setValue }
}
